import { makeStyles } from 'tss-react/mui';
import { createContext, Dispatch, lazy, SetStateAction, Suspense, useMemo, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import AppBar from 'web-library/stories/atom/AppBar/AppBar';
import Box from 'web-library/stories/atom/Box/Box';
import WidthGuard from 'web-library/stories/atom/WidthGuard';
import BreadCrumbs from '../components/bread-crumbs/BreadCrumbs';
import DefaultHeader, { UserDetailsData } from '../components/DefaultHeader';
import { OrganisationAccess, Role } from '../models';
import PrivateRoute from '../modules/profile/components/PrivateRoute';
import PrivateOrganisationRoute from '../modules/profile/components/PrivateOrganisationRoute';
import RouteKey from './key';

const PrivacyStatement = lazy(() => import('../pages/helpdesk/PrivacyStatement'));
const TermsOfUse = lazy(() => import('../pages/helpdesk/TermsOfUse'));
const Faq = lazy(() => import('../pages/helpdesk/Faq'));
const AccessArrangmentRoute = lazy(() => import('./AccessArrangmentRoute'));
const ResultRoute = lazy(() => import('./ResultRoute'));
const ResitRegistrationRoute = lazy(() => import('./ResitRegistrationRoute'));
const ErrorRoute = lazy(() => import('./ErrorRoute'));
const AppealRoute = lazy(() => import('./AppealRoute'));
const TransactionRoute = lazy(() => import('./TransactionRoute'));
const StatementOfResultsRoute = lazy(() => import('./StatementOfResultsRoute'));
const AnnouncementRoute = lazy(() => import('./AnnouncementRoute'));
const UsefulInformationRoute = lazy(() => import('./UsefulInformationRoute'));
const UniqueLinkRoute = lazy(() => import('./UniqueLinkRoute'));
const SpecialConsiderationRoute = lazy(() => import('./SpecialConsiderationRoute'));
const Homepage = lazy(() => import('../pages/home/Homepage'));
const WithdrawalRoute = lazy(() => import('./WithdrawalRoute'));
const AdminRoute = lazy(() => import('./AdminRoute'));
const ExamRoute = lazy(() => import('./ExamRoute'));
const ResitRoute = lazy(() => import('./ResitRoute'));
const SubjectAmendmentRoute = lazy(() => import('./SubjectAmendmentRoute'));
const ProfileRoute = lazy(() => import('./ProfileRoute'));

const useStyles = makeStyles()((theme) => ({
  rootContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 720,
    height: 'auto',
    zIndex: 100,
    [theme.breakpoints.between('xs', 'md')]: {
      minHeight: 600,
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 480,
    },
  },
}));

const {
  profile,
  accessArrangement,
  admin,
  appeal,
  exam,
  resit,
  resitRegistration,
  result,
  faq,
  privacyStatement,
  termsOfUse,
  amendment,
  withdrawal,
  error: errorRoute,
  uniqueLink,
  specialConsideration,
  statementOfResults,
  usefulInformation,
  announcement,
  transaction,
} = RouteKey.child;

interface isNonNexPassportDisable {
  isPassportDisabled: Boolean;
  setIsPassportDisabled: Dispatch<SetStateAction<Boolean>>;
}

export const NonNexPassportDisableContext = createContext<isNonNexPassportDisable>({
  isPassportDisabled: false,
  setIsPassportDisabled: () => null,
});

/**
 * Root router component.
 *
 * Data & isSuccess from user details API to be used in DefaultHeader.tsx. Reason can be found in
 * documentation in DefaultHeader.tsx
 *
 * @param data data from user details API
 * @param isSuccess indicator whether user details API call is success
 */
const RootRouter = ({ data, isSuccess }: UserDetailsData) => {
  const { classes } = useStyles();

  const [isPassportDisabled, setIsPassportDisabled] = useState<Boolean>(false);

  const contextValue = useMemo(
    () => ({
      isPassportDisabled,
      setIsPassportDisabled,
    }),
    [isPassportDisabled]
  );

  return (
    <NonNexPassportDisableContext.Provider value={contextValue}>
      <Box className={classes.rootContainer}>
        <AppBar position="static" elevation={0}>
          <WidthGuard>
            <DefaultHeader data={data} isSuccess={isSuccess} />
          </WidthGuard>
        </AppBar>

        <BreadCrumbs separator="NextIcon" />
        <Suspense fallback={null}>
          <Switch>
            <PrivateRoute
              exact
              path="/"
              roles={[Role.candidate, Role.coordinator, Role.opsAdmin, Role.sysAdmin]}
            >

              <Homepage />
            </PrivateRoute>

            <Route path={`/${profile}`}>
              <ProfileRoute />
            </Route>

            <Route path={`/${amendment}`}>
              <SubjectAmendmentRoute />
            </Route>

            <Route path={`/${withdrawal}`}>
              <WithdrawalRoute />
            </Route>

            <PrivateRoute path={`/${accessArrangement}`} roles={[Role.candidate, Role.coordinator]}>
              <PrivateOrganisationRoute
                path={`/${accessArrangement}`}
                organisationAccess={[OrganisationAccess.accessArrangement]}
              >
                <AccessArrangmentRoute />
              </PrivateOrganisationRoute>
            </PrivateRoute>

            <PrivateRoute path={`/${appeal}`} roles={[Role.candidate, Role.coordinator]}>
              <AppealRoute />
            </PrivateRoute>

            <PrivateRoute path={`/${result}`} roles={[Role.candidate, Role.coordinator]}>
              <PrivateOrganisationRoute
                path={`/${result}`}
                organisationAccess={[OrganisationAccess.resultRelease]}
              >
                <ResultRoute />
              </PrivateOrganisationRoute>
            </PrivateRoute>

            <PrivateRoute
              path={`/${resit}`}
              roles={[Role.candidate, Role.coordinator, Role.opsAdmin]}
            >
              <ResitRoute />
            </PrivateRoute>

            <PrivateRoute path={`/${exam}`} roles={[Role.candidate, Role.coordinator]}>
              <ExamRoute />
            </PrivateRoute>

            <PrivateRoute path={`/${uniqueLink}`} roles={[Role.candidate, Role.coordinator]}>
              <UniqueLinkRoute />
            </PrivateRoute>

            <PrivateRoute
              path={`/${specialConsideration}`}
              roles={[Role.candidate, Role.coordinator]}
            >
              <SpecialConsiderationRoute />
            </PrivateRoute>

            <PrivateRoute
              path={`/${statementOfResults}`}
              roles={[Role.candidate, Role.coordinator]}
            >
              <StatementOfResultsRoute />
            </PrivateRoute>

            <PrivateRoute path={`/${usefulInformation}`} roles={[Role.candidate]}>
              <UsefulInformationRoute />
            </PrivateRoute>

            <PrivateRoute path={`/${announcement}`} roles={[Role.candidate, Role.coordinator]}>
              <AnnouncementRoute />
            </PrivateRoute>

            <PrivateRoute path={`/${transaction}`} roles={[Role.candidate, Role.coordinator]}>
              <TransactionRoute />
            </PrivateRoute>

            {/* Show receipt page for resit registration, mainly used for link in e-mail that was sent out */}
            <Route path={`/${resitRegistration}`}>
              <ResitRegistrationRoute />
            </Route>

            <PrivateRoute path={`/${admin}`} roles={[Role.opsAdmin, Role.sysAdmin]}>
              <AdminRoute />
            </PrivateRoute>

            <Route path={`/${errorRoute}`}>
              <ErrorRoute />
            </Route>
            <Route path={[`/${faq}/`, `/${faq}`]}>
              <Faq />
            </Route>
            <Route path={`/${privacyStatement}`}>
              <PrivacyStatement />
            </Route>
            <Route path={`/${termsOfUse}`}>
              <TermsOfUse />
            </Route>

            <Route path="*" exact>
              <Redirect to={`/${errorRoute}`} />
            </Route>
          </Switch>
        </Suspense>
      </Box>
    </NonNexPassportDisableContext.Provider>
  );
};

export default RootRouter;
